import { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { 
    DollarSign, TrendingUp, TrendingDown, PieChart, Plus, X, Calendar,
    ArrowUpCircle, ArrowDownCircle, Wallet, Receipt, Package, Home, 
    Zap, Users, FileText, Percent, Wrench, Filter, RefreshCw, Download,
    Printer, Banknote
} from 'lucide-react';
import { API_URL } from '../config';
import { formatFechaHoraChile } from '../utils/fechasChile';
import { formatDesgloseDetalle } from '../utils/denominacionesClp';
import { printComprobanteMovimientoEfectivo } from '../utils/printMovimientoEfectivo';

export default function FlujoCajaPage() {
    const [movimientos, setMovimientos] = useState([]);
    const [movimientosPos, setMovimientosPos] = useState([]);
    const [resumenPos, setResumenPos] = useState(null);
    const [categorias, setCategorias] = useState([]);
    const [resumen, setResumen] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingPos, setLoadingPos] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [activeTab, setActiveTab] = useState('dashboard');
    
    // Filters
    const [fechaInicio, setFechaInicio] = useState(() => {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
    });
    const [fechaFin, setFechaFin] = useState(() => new Date().toISOString().split('T')[0]);
    const [filterTipo, setFilterTipo] = useState('');
    const [filterCategoria, setFilterCategoria] = useState('');

    // Form state
    const [formData, setFormData] = useState({
        fecha: new Date().toISOString().split('T')[0],
        tipo: 'egreso',
        categoria: '',
        subcategoria: '',
        descripcion: '',
        monto: '',
        comprobante: '',
        notas: ''
    });

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        fetchCategorias();
        fetchResumen();
        fetchMovimientos();
        fetchMovimientosPos();
    }, [fechaInicio, fechaFin]);

    const fetchCategorias = async () => {
        try {
            const res = await fetch(`${API_URL}/api/flujo-caja/categorias`, { headers });
            const data = await res.json();
            setCategorias(data);
        } catch (err) {
            console.error('Error fetching categorias:', err);
        }
    };

    const fetchResumen = async () => {
        try {
            const res = await fetch(
                `${API_URL}/api/flujo-caja/resumen?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`, 
                { headers }
            );
            const data = await res.json();
            setResumen(data);
        } catch (err) {
            console.error('Error fetching resumen:', err);
        }
    };

    const fetchMovimientos = async () => {
        setLoading(true);
        try {
            let url = `${API_URL}/api/flujo-caja?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`;
            if (filterTipo) url += `&tipo=${filterTipo}`;
            if (filterCategoria) url += `&categoria=${filterCategoria}`;
            
            const res = await fetch(url, { headers });
            const data = await res.json();
            setMovimientos(data);
        } catch (err) {
            console.error('Error fetching movimientos:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchMovimientosPos = async () => {
        setLoadingPos(true);
        try {
            const res = await fetch(
                `${API_URL}/api/flujo-caja/movimientos-pos?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`,
                { headers }
            );
            const data = await res.json();
            setMovimientosPos(Array.isArray(data.movimientos) ? data.movimientos : []);
            setResumenPos(data.resumen || null);
        } catch (err) {
            console.error('Error fetching movimientos POS:', err);
            setMovimientosPos([]);
            setResumenPos(null);
        } finally {
            setLoadingPos(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingItem 
                ? `${API_URL}/api/flujo-caja/${editingItem.id}`
                : `${API_URL}/api/flujo-caja`;
            
            const res = await fetch(url, {
                method: editingItem ? 'PUT' : 'POST',
                headers,
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                setShowModal(false);
                resetForm();
                fetchMovimientos();
                fetchResumen();
            }
        } catch (err) {
            console.error('Error saving movimiento:', err);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Eliminar este movimiento?')) return;
        try {
            await fetch(`${API_URL}/api/flujo-caja/${id}`, { method: 'DELETE', headers });
            fetchMovimientos();
            fetchResumen();
        } catch (err) {
            console.error('Error deleting:', err);
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setFormData({
            fecha: item.fecha?.split('T')[0] || '',
            tipo: item.tipo,
            categoria: item.categoria,
            subcategoria: item.subcategoria || '',
            descripcion: item.descripcion || '',
            monto: item.monto,
            comprobante: item.comprobante || '',
            notas: item.notas || ''
        });
        setShowModal(true);
    };

    const resetForm = () => {
        setEditingItem(null);
        setFormData({
            fecha: new Date().toISOString().split('T')[0],
            tipo: 'egreso',
            categoria: '',
            subcategoria: '',
            descripcion: '',
            monto: '',
            comprobante: '',
            notas: ''
        });
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value || 0);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('es-CL');
    };

    const getCategoryIcon = (codigo) => {
        const icons = {
            venta: Receipt,
            otro_ingreso: Plus,
            costo_directo: Package,
            gasto_indirecto: Wallet,
            arriendo: Home,
            servicio: Zap,
            sueldo: Users,
            tramite_asesoria: FileText,
            impuesto: Percent,
            mantencion: Wrench
        };
        return icons[codigo] || DollarSign;
    };

    const columns = [
        {
            name: 'Fecha',
            selector: row => row.fecha,
            sortable: true,
            width: '110px',
            cell: row => formatDate(row.fecha)
        },
        {
            name: 'Tipo',
            selector: row => row.tipo,
            sortable: true,
            width: '100px',
            cell: row => (
                <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    color: row.tipo === 'ingreso' ? '#10b981' : '#ef4444',
                    fontWeight: '600',
                    fontSize: '0.8rem'
                }}>
                    {row.tipo === 'ingreso' ? <ArrowUpCircle size={14} /> : <ArrowDownCircle size={14} />}
                    {row.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}
                </span>
            )
        },
        {
            name: 'Categoría',
            selector: row => row.categoria_nombre,
            sortable: true,
            cell: row => {
                const IconComponent = getCategoryIcon(row.categoria);
                return (
                    <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.25rem 0.5rem',
                        background: `${row.categoria_color}20`,
                        color: row.categoria_color,
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: '500'
                    }}>
                        <IconComponent size={14} />
                        {row.categoria_nombre}
                    </span>
                );
            }
        },
        {
            name: 'Descripción',
            selector: row => row.descripcion,
            sortable: true,
            grow: 2,
            cell: row => (
                <div style={{ fontSize: '0.85rem' }}>
                    <div>{row.descripcion || '-'}</div>
                    {row.subcategoria && (
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{row.subcategoria}</div>
                    )}
                </div>
            )
        },
        {
            name: 'Monto',
            selector: row => parseFloat(row.monto),
            sortable: true,
            width: '140px',
            cell: row => (
                <span style={{
                    fontWeight: '700',
                    color: row.tipo === 'ingreso' ? '#10b981' : '#ef4444',
                    fontSize: '0.9rem',
                    display: 'block',
                    textAlign: 'right'
                }}>
                    {row.tipo === 'ingreso' ? '+' : '-'}{formatCurrency(row.monto)}
                </span>
            )
        },
        {
            name: 'Acciones',
            width: '100px',
            cell: row => (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => handleEdit(row)} style={styles.actionBtn}>Editar</button>
                    <button onClick={() => handleDelete(row.id)} style={{...styles.actionBtn, color: '#ef4444'}}>×</button>
                </div>
            )
        }
    ];

    const posColumns = [
        {
            name: 'Fecha y hora',
            selector: row => row.created_at,
            sortable: true,
            width: '160px',
            cell: row => formatFechaHoraChile(row.created_at) || '—'
        },
        {
            name: 'Caja',
            selector: row => row.caja_nombre,
            sortable: true,
            width: '120px',
            cell: row => row.caja_nombre || '—'
        },
        {
            name: 'Responsable',
            selector: row => row.responsable,
            sortable: true,
            cell: row => row.responsable || '—'
        },
        {
            name: 'Tipo',
            selector: row => row.tipo,
            sortable: true,
            width: '130px',
            cell: row => (
                <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    color: row.tipo === 'ingreso' ? '#10b981' : '#b45309',
                    fontWeight: '600',
                    fontSize: '0.8rem'
                }}>
                    {row.tipo === 'ingreso' ? <ArrowUpCircle size={14} /> : <ArrowDownCircle size={14} />}
                    {row.etiqueta}
                </span>
            )
        },
        {
            name: 'Desglose',
            selector: row => formatDesgloseDetalle(row.desglose),
            cell: row => formatDesgloseDetalle(row.desglose) || 'Sin desglose'
        },
        {
            name: 'Monto',
            selector: row => row.monto,
            sortable: true,
            width: '130px',
            right: true,
            cell: row => (
                <span style={{
                    fontWeight: '700',
                    color: row.tipo === 'ingreso' ? '#10b981' : '#b45309',
                    fontSize: '0.9rem',
                    display: 'block',
                    textAlign: 'right'
                }}>
                    {row.tipo === 'ingreso' ? '+' : '−'}{formatCurrency(row.monto)}
                </span>
            )
        },
        {
            name: '',
            width: '110px',
            cell: row => (
                <button
                    type="button"
                    onClick={() => printComprobanteMovimientoEfectivo(row)}
                    style={styles.printBtn}
                >
                    <Printer size={14} /> Imprimir
                </button>
            )
        }
    ];

    const ingresos = categorias.filter(c => c.tipo === 'ingreso');
    const egresos = categorias.filter(c => c.tipo === 'egreso');

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>
                        <Wallet size={28} style={{ color: 'var(--primary-color)' }} />
                        Flujo de Caja
                    </h1>
                    <p style={styles.subtitle}>Control de ingresos, gastos, utilidad e inyecciones/retiros del POS</p>
                </div>
                <button onClick={() => { resetForm(); setShowModal(true); }} style={styles.addBtn}>
                    <Plus size={20} /> Nuevo Movimiento
                </button>
            </div>

            {/* Tabs */}
            <div style={styles.tabs}>
                <button 
                    onClick={() => setActiveTab('dashboard')} 
                    style={{...styles.tab, ...(activeTab === 'dashboard' ? styles.tabActive : {})}}
                >
                    <PieChart size={18} /> Dashboard
                </button>
                <button 
                    onClick={() => setActiveTab('movimientos')} 
                    style={{...styles.tab, ...(activeTab === 'movimientos' ? styles.tabActive : {})}}
                >
                    <FileText size={18} /> Movimientos
                </button>
                <button 
                    onClick={() => setActiveTab('cajon-pos')} 
                    style={{...styles.tab, ...(activeTab === 'cajon-pos' ? styles.tabActive : {})}}
                >
                    <Banknote size={18} /> Cajón POS
                </button>
            </div>

            {/* Date Filters */}
            <div style={styles.filterBar}>
                <div style={styles.filterGroup}>
                    <Calendar size={18} style={{ color: '#64748b' }} />
                    <input 
                        type="date" 
                        value={fechaInicio} 
                        onChange={(e) => setFechaInicio(e.target.value)}
                        style={styles.dateInput}
                    />
                    <span style={{ color: '#64748b' }}>a</span>
                    <input 
                        type="date" 
                        value={fechaFin} 
                        onChange={(e) => setFechaFin(e.target.value)}
                        style={styles.dateInput}
                    />
                </div>
                <button onClick={() => { fetchMovimientos(); fetchResumen(); fetchMovimientosPos(); }} style={styles.refreshBtn}>
                    <RefreshCw size={16} /> Actualizar
                </button>
            </div>

            {activeTab === 'dashboard' && resumen && (
                <>
                    {/* Summary Cards */}
                    <div style={styles.summaryGrid}>
                        <div style={{...styles.summaryCard, borderLeftColor: '#10b981'}}>
                            <div style={styles.cardIcon}>
                                <TrendingUp size={24} color="#10b981" />
                            </div>
                            <div>
                                <p style={styles.cardLabel}>Ingresos</p>
                                <p style={{...styles.cardValue, color: '#10b981'}}>{formatCurrency(resumen.resumen.ingresos)}</p>
                            </div>
                        </div>
                        
                        <div style={{...styles.summaryCard, borderLeftColor: '#ef4444'}}>
                            <div style={styles.cardIcon}>
                                <TrendingDown size={24} color="#ef4444" />
                            </div>
                            <div>
                                <p style={styles.cardLabel}>Egresos</p>
                                <p style={{...styles.cardValue, color: '#ef4444'}}>{formatCurrency(resumen.resumen.egresos)}</p>
                            </div>
                        </div>

                        <div style={{...styles.summaryCard, borderLeftColor: '#F5B700'}}>
                            <div style={styles.cardIcon}>
                                <DollarSign size={24} color="#F5B700" />
                            </div>
                            <div>
                                <p style={styles.cardLabel}>Utilidad</p>
                                <p style={{
                                    ...styles.cardValue, 
                                    color: resumen.resumen.utilidad >= 0 ? '#10b981' : '#ef4444'
                                }}>
                                    {formatCurrency(resumen.resumen.utilidad)}
                                </p>
                            </div>
                        </div>

                        <div style={{...styles.summaryCard, borderLeftColor: '#f59e0b'}}>
                            <div style={styles.cardIcon}>
                                <Receipt size={24} color="#f59e0b" />
                            </div>
                            <div>
                                <p style={styles.cardLabel}>Facturado (Ventas)</p>
                                <p style={{...styles.cardValue, color: '#f59e0b'}}>{formatCurrency(resumen.resumen.facturado)}</p>
                            </div>
                        </div>
                    </div>

                    {resumenPos && (
                        <div style={{...styles.summaryGrid, marginTop: '1rem'}}>
                            <div style={{...styles.summaryCard, borderLeftColor: '#047857'}}>
                                <div style={styles.cardIcon}>
                                    <Banknote size={24} color="#047857" />
                                </div>
                                <div>
                                    <p style={styles.cardLabel}>Inyecciones POS</p>
                                    <p style={{...styles.cardValue, color: '#047857'}}>{formatCurrency(resumenPos.inyecciones)}</p>
                                </div>
                            </div>
                            <div style={{...styles.summaryCard, borderLeftColor: '#b45309'}}>
                                <div style={styles.cardIcon}>
                                    <ArrowDownCircle size={24} color="#b45309" />
                                </div>
                                <div>
                                    <p style={styles.cardLabel}>Retiros POS</p>
                                    <p style={{...styles.cardValue, color: '#b45309'}}>{formatCurrency(resumenPos.retiros)}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Breakdown by Category */}
                    <div style={styles.section}>
                        <h3 style={styles.sectionTitle}>Desglose por Categoría</h3>
                        <div style={styles.categoryGrid}>
                            {resumen.por_categoria.map((cat, idx) => {
                                const IconComponent = getCategoryIcon(cat.categoria);
                                return (
                                    <div key={idx} style={styles.categoryCard}>
                                        <div style={{
                                            ...styles.categoryIcon,
                                            background: `${cat.color}20`,
                                            color: cat.color
                                        }}>
                                            <IconComponent size={20} />
                                        </div>
                                        <div style={styles.categoryInfo}>
                                            <span style={styles.categoryName}>{cat.categoria_nombre}</span>
                                            <span style={{
                                                ...styles.categoryAmount,
                                                color: cat.tipo === 'ingreso' ? '#10b981' : '#ef4444'
                                            }}>
                                                {cat.tipo === 'ingreso' ? '+' : '-'}{formatCurrency(cat.total)}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                            {resumen.por_categoria.length === 0 && (
                                <p style={{ color: '#64748b', padding: '2rem', textAlign: 'center', width: '100%' }}>
                                    No hay movimientos en este período
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Margin Info */}
                    <div style={styles.marginBox}>
                        <div style={styles.marginItem}>
                            <span>Margen de Utilidad</span>
                            <span style={{
                                fontWeight: '700',
                                fontSize: '1.5rem',
                                color: parseFloat(resumen.resumen.margen) >= 0 ? '#10b981' : '#ef4444'
                            }}>
                                {resumen.resumen.margen}%
                            </span>
                        </div>
                        <div style={styles.marginDivider}></div>
                        <div style={styles.marginFormula}>
                            <code style={styles.code}>
                                Utilidad = Ingresos ({formatCurrency(resumen.resumen.ingresos)}) - Egresos ({formatCurrency(resumen.resumen.egresos)})
                            </code>
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'movimientos' && (
                <div style={styles.section}>
                    {/* Extra Filters */}
                    <div style={styles.extraFilters}>
                        <select 
                            value={filterTipo} 
                            onChange={(e) => { setFilterTipo(e.target.value); }}
                            style={styles.filterSelect}
                        >
                            <option value="">Todos los tipos</option>
                            <option value="ingreso">Ingresos</option>
                            <option value="egreso">Egresos</option>
                        </select>
                        <select 
                            value={filterCategoria} 
                            onChange={(e) => { setFilterCategoria(e.target.value); }}
                            style={styles.filterSelect}
                        >
                            <option value="">Todas las categorías</option>
                            {categorias.map(cat => (
                                <option key={cat.id} value={cat.codigo}>{cat.nombre}</option>
                            ))}
                        </select>
                        <button onClick={fetchMovimientos} style={styles.filterBtn}>
                            <Filter size={16} /> Filtrar
                        </button>
                    </div>

                    <DataTable
                        columns={columns}
                        data={movimientos}
                        pagination
                        paginationPerPage={15}
                        progressPending={loading}
                        noDataComponent={
                            <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                                <Wallet size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                                <p>No hay movimientos registrados</p>
                            </div>
                        }
                        customStyles={tableStyles}
                    />
                </div>
            )}

            {activeTab === 'cajon-pos' && (
                <div style={styles.section}>
                    <p style={{ color: '#64748b', margin: '0 0 1rem', fontSize: '0.95rem' }}>
                        Inyecciones y retiros por seguridad registrados en caja. Hora de Chile. No se mezclan con la utilidad del negocio.
                    </p>
                    {resumenPos && (
                        <div style={{...styles.summaryGrid, marginBottom: '1.25rem'}}>
                            <div style={{...styles.summaryCard, borderLeftColor: '#047857'}}>
                                <div>
                                    <p style={styles.cardLabel}>Inyecciones</p>
                                    <p style={{...styles.cardValue, color: '#047857'}}>{formatCurrency(resumenPos.inyecciones)}</p>
                                </div>
                            </div>
                            <div style={{...styles.summaryCard, borderLeftColor: '#b45309'}}>
                                <div>
                                    <p style={styles.cardLabel}>Retiros</p>
                                    <p style={{...styles.cardValue, color: '#b45309'}}>{formatCurrency(resumenPos.retiros)}</p>
                                </div>
                            </div>
                            <div style={{...styles.summaryCard, borderLeftColor: '#334155'}}>
                                <div>
                                    <p style={styles.cardLabel}>Movimientos</p>
                                    <p style={{...styles.cardValue, color: '#334155'}}>{resumenPos.count}</p>
                                </div>
                            </div>
                        </div>
                    )}
                    <DataTable
                        columns={posColumns}
                        data={movimientosPos}
                        pagination
                        paginationPerPage={15}
                        progressPending={loadingPos}
                        noDataComponent={
                            <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                                <Banknote size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                                <p>No hay inyecciones ni retiros de POS en este período</p>
                            </div>
                        }
                        customStyles={tableStyles}
                    />
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modal}>
                        <div style={styles.modalHeader}>
                            <h3>{editingItem ? 'Editar Movimiento' : 'Nuevo Movimiento'}</h3>
                            <button onClick={() => setShowModal(false)} style={styles.closeBtn}>
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} style={styles.form}>
                            <div style={styles.formRow}>
                                <div style={styles.formGroup}>
                                    <label>Fecha</label>
                                    <input
                                        type="date"
                                        value={formData.fecha}
                                        onChange={(e) => setFormData({...formData, fecha: e.target.value})}
                                        required
                                        style={styles.input}
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label>Tipo</label>
                                    <select
                                        value={formData.tipo}
                                        onChange={(e) => setFormData({...formData, tipo: e.target.value, categoria: ''})}
                                        required
                                        style={styles.input}
                                    >
                                        <option value="ingreso">Ingreso</option>
                                        <option value="egreso">Egreso</option>
                                    </select>
                                </div>
                            </div>

                            <div style={styles.formGroup}>
                                <label>Categoría</label>
                                <select
                                    value={formData.categoria}
                                    onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                                    required
                                    style={styles.input}
                                >
                                    <option value="">Seleccionar categoría</option>
                                    {formData.tipo === 'ingreso' ? (
                                        ingresos.map(cat => (
                                            <option key={cat.id} value={cat.codigo}>{cat.nombre}</option>
                                        ))
                                    ) : (
                                        egresos.map(cat => (
                                            <option key={cat.id} value={cat.codigo}>{cat.nombre}</option>
                                        ))
                                    )}
                                </select>
                            </div>

                            <div style={styles.formRow}>
                                <div style={styles.formGroup}>
                                    <label>Subcategoría (opcional)</label>
                                    <input
                                        type="text"
                                        value={formData.subcategoria}
                                        onChange={(e) => setFormData({...formData, subcategoria: e.target.value})}
                                        placeholder="Ej: Agua, Luz, etc."
                                        style={styles.input}
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label>Monto</label>
                                    <input
                                        type="number"
                                        value={formData.monto}
                                        onChange={(e) => setFormData({...formData, monto: e.target.value})}
                                        required
                                        min="0"
                                        step="1"
                                        placeholder="0"
                                        style={styles.input}
                                    />
                                </div>
                            </div>

                            <div style={styles.formGroup}>
                                <label>Descripción</label>
                                <input
                                    type="text"
                                    value={formData.descripcion}
                                    onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                                    placeholder="Descripción del movimiento"
                                    style={styles.input}
                                />
                            </div>

                            <div style={styles.formRow}>
                                <div style={styles.formGroup}>
                                    <label>N° Comprobante (opcional)</label>
                                    <input
                                        type="text"
                                        value={formData.comprobante}
                                        onChange={(e) => setFormData({...formData, comprobante: e.target.value})}
                                        placeholder="Factura, boleta, etc."
                                        style={styles.input}
                                    />
                                </div>
                            </div>

                            <div style={styles.formGroup}>
                                <label>Notas (opcional)</label>
                                <textarea
                                    value={formData.notas}
                                    onChange={(e) => setFormData({...formData, notas: e.target.value})}
                                    placeholder="Notas adicionales..."
                                    style={{...styles.input, minHeight: '80px', resize: 'vertical'}}
                                />
                            </div>

                            <div style={styles.formActions}>
                                <button type="button" onClick={() => setShowModal(false)} style={styles.cancelBtn}>
                                    Cancelar
                                </button>
                                <button type="submit" style={styles.submitBtn}>
                                    {editingItem ? 'Guardar Cambios' : 'Registrar Movimiento'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    container: {
        padding: '1.5rem',
        maxWidth: '1400px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem',
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        fontSize: '1.75rem',
        fontWeight: '700',
        color: 'var(--text-primary)',
        margin: 0,
    },
    subtitle: {
        color: 'var(--text-secondary)',
        margin: '0.25rem 0 0 0',
        fontSize: '0.95rem',
    },
    addBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.75rem 1.25rem',
        background: 'linear-gradient(135deg, #F5B700, #C28C00)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontWeight: '600',
        cursor: 'pointer',
        boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)',
    },
    tabs: {
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        borderBottom: '1px solid #e2e8f0',
        paddingBottom: '0.5rem',
    },
    tab: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.75rem 1.25rem',
        background: 'transparent',
        border: 'none',
        borderRadius: '8px',
        color: '#64748b',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    tabActive: {
        background: 'var(--primary-color)',
        color: 'white',
    },
    filterBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        padding: '1rem',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        flexWrap: 'wrap',
        gap: '1rem',
    },
    filterGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
    },
    dateInput: {
        padding: '0.5rem 0.75rem',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '0.9rem',
    },
    refreshBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 1rem',
        background: '#f1f5f9',
        border: 'none',
        borderRadius: '8px',
        color: '#475569',
        fontWeight: '500',
        cursor: 'pointer',
    },
    summaryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem',
    },
    summaryCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '1.25rem',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        borderLeft: '4px solid',
    },
    cardIcon: {
        padding: '0.75rem',
        borderRadius: '10px',
        background: '#f8fafc',
    },
    cardLabel: {
        margin: 0,
        fontSize: '0.85rem',
        color: '#64748b',
    },
    cardValue: {
        margin: '0.25rem 0 0 0',
        fontSize: '1.5rem',
        fontWeight: '700',
    },
    section: {
        background: 'white',
        borderRadius: '12px',
        padding: '1.5rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        marginBottom: '1.5rem',
    },
    sectionTitle: {
        fontSize: '1.1rem',
        fontWeight: '600',
        color: 'var(--text-primary)',
        margin: '0 0 1rem 0',
    },
    categoryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
        gap: '1rem',
    },
    categoryCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '1rem',
        background: '#f8fafc',
        borderRadius: '10px',
    },
    categoryIcon: {
        padding: '0.5rem',
        borderRadius: '8px',
    },
    categoryInfo: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    categoryName: {
        fontSize: '0.85rem',
        color: '#64748b',
    },
    categoryAmount: {
        fontSize: '1.1rem',
        fontWeight: '700',
    },
    marginBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '2rem',
        padding: '1.5rem',
        background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
        borderRadius: '12px',
        flexWrap: 'wrap',
    },
    marginItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem',
    },
    marginDivider: {
        width: '1px',
        height: '40px',
        background: '#e2e8f0',
    },
    marginFormula: {
        flex: 1,
    },
    code: {
        padding: '0.5rem 1rem',
        background: 'white',
        borderRadius: '6px',
        fontSize: '0.85rem',
        color: '#475569',
        fontFamily: 'monospace',
    },
    extraFilters: {
        display: 'flex',
        gap: '1rem',
        marginBottom: '1rem',
        flexWrap: 'wrap',
    },
    filterSelect: {
        padding: '0.5rem 1rem',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '0.9rem',
        minWidth: '180px',
    },
    filterBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 1rem',
        background: 'var(--primary-color)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontWeight: '500',
        cursor: 'pointer',
    },
    actionBtn: {
        padding: '0.25rem 0.5rem',
        background: 'transparent',
        border: 'none',
        color: 'var(--primary-color)',
        cursor: 'pointer',
        fontSize: '0.8rem',
        fontWeight: '500',
    },
    printBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.35rem',
        padding: '0.4rem 0.65rem',
        background: '#fff',
        border: '1px solid #cbd5e1',
        borderRadius: '8px',
        color: '#334155',
        cursor: 'pointer',
        fontSize: '0.8rem',
        fontWeight: '600',
    },
    modalOverlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
    },
    modal: {
        background: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '550px',
        maxHeight: '90vh',
        overflow: 'auto',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.25rem 1.5rem',
        borderBottom: '1px solid #e2e8f0',
    },
    closeBtn: {
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: '#64748b',
        padding: '0.25rem',
    },
    form: {
        padding: '1.5rem',
    },
    formRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1rem',
    },
    formGroup: {
        marginBottom: '1rem',
    },
    input: {
        width: '100%',
        padding: '0.75rem',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '0.95rem',
        boxSizing: 'border-box',
    },
    formActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '1rem',
        marginTop: '1rem',
        paddingTop: '1rem',
        borderTop: '1px solid #e2e8f0',
    },
    cancelBtn: {
        padding: '0.75rem 1.5rem',
        background: '#f1f5f9',
        border: 'none',
        borderRadius: '8px',
        color: '#475569',
        fontWeight: '500',
        cursor: 'pointer',
    },
    submitBtn: {
        padding: '0.75rem 1.5rem',
        background: 'linear-gradient(135deg, #F5B700, #C28C00)',
        border: 'none',
        borderRadius: '8px',
        color: 'white',
        fontWeight: '600',
        cursor: 'pointer',
    },
};

const tableStyles = {
    headCells: {
        style: {
            fontWeight: '600',
            fontSize: '0.85rem',
            color: '#475569',
            background: '#f8fafc',
        },
    },
    cells: {
        style: {
            fontSize: '0.9rem',
        },
    },
};
